import React from "react";
import { brandInk, mix, onBrand } from "../../utils/brandTone";

// Two small objects that tell a student where their lessons stand.
//
// A coin is a lesson they have bought and not yet taken. A stamp is a lesson
// they have taken: the same lesson, turned over and kept. Both wear the
// colour the studio gave that kind of lesson, so someone with private
// lessons and coaching sees two colours and never has to read which is
// which. A studio's colour can be pale, so the rim is always a darker mix
// of it and any ink on it goes through brandTone.

const INK = "#1a1a1a";

export const Coin = ({ color = INK, size = 18, className, style }) => (
  <span
    aria-hidden
    className={className}
    style={{
      display: "inline-block",
      width: size,
      height: size,
      borderRadius: "50%",
      flexShrink: 0,
      background: `radial-gradient(circle at 34% 28%, ${mix(color, "#ffffff", 0.38)} 0%, ${color} 62%, ${mix(color, "#000000", 0.18)} 100%)`,
      boxShadow: [
        `0 0 0 1px ${mix(color, "#000000", 0.3)}`,
        `inset 0 0 0 ${Math.max(1.5, size * 0.13)}px ${mix(color, "#ffffff", 0.3)}`,
        `inset 0 0 0 ${Math.max(2.5, size * 0.19)}px ${mix(color, "#000000", 0.08)}`,
        "0 1px 2px rgba(0,0,0,.22)",
      ].join(", "),
      ...style,
    }}
  />
);

// A lesson taken, with its number in the order they were earned. A
// milestone (the first, the tenth, the fiftieth) is inked solid.
export const Stamp = ({ color = INK, number, size = 56, milestone = false, className, style }) => {
  const ink = brandInk(color);
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        boxSizing: "border-box",
        fontFamily: "'Nunito', 'Varela Round', sans-serif",
        fontWeight: 700,
        fontSize: size * (String(number).length > 2 ? 0.3 : 0.36),
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1,
        color: milestone ? onBrand(color) : ink,
        background: milestone ? color : mix(color, "#ffffff", 0.9),
        border: `2px solid ${milestone ? mix(color, "#000000", 0.25) : mix(ink, "#ffffff", 0.25)}`,
        boxShadow: milestone
          ? `0 0 0 3px #ffffff, 0 0 0 5px ${mix(color, "#ffffff", 0.35)}`
          : `inset 0 0 0 3px #ffffff, inset 0 0 0 4px ${mix(ink, "#ffffff", 0.6)}`,
        ...style,
      }}
    >
      {number}
    </span>
  );
};

// A lesson booked and not yet taken: the place its stamp will go.
export const EmptyStamp = ({ color = INK, size = 56, style }) => (
  <span
    aria-hidden
    style={{
      display: "inline-block",
      width: size,
      height: size,
      borderRadius: "50%",
      flexShrink: 0,
      boxSizing: "border-box",
      border: `2px dashed ${mix(brandInk(color), "#ffffff", 0.45)}`,
      background: "#ffffff",
      ...style,
    }}
  />
);

export default Coin;
